import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { map, filter, take, switchMap } from 'rxjs/operators';
import { AuthSelectors } from '@/app/+state/auth';
import { PermissionsService, CaslAction, RbacSubjects } from '@/app/shared/services';
import { MessageService } from 'primeng/api';

/**
 * Generic RBAC guard factory that can be configured for different permissions
 */
export function createRbacGuard(
    action: CaslAction,
    subject: RbacSubjects,
    redirectTo: string = '/unauthorized'
): CanActivateFn {
    return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        const store = inject(Store);
        const router = inject(Router);
        const permissionsService = inject(PermissionsService);
        const messageService = inject(MessageService);

        return store.select(AuthSelectors.selectAuthInitialized).pipe(
            filter((initialized) => initialized),
            take(1),
            switchMap(() => store.select(AuthSelectors.selectUser)),
            take(1),
            map((user) => {
                if (!user) {
                    // User not logged in, redirect to login
                    return router.createUrlTree(['/auth/login']);
                }

                const hasPermission = permissionsService.can(action, subject);
                
                if (!hasPermission) {
                    // Show error message
                    messageService.add({
                        severity: 'error',
                        summary: 'Access Denied',
                        detail: `You don't have permission to ${action} ${subject}`,
                        life: 5000
                    });
                    
                    // Redirect to appropriate page
                    return router.createUrlTree([redirectTo]);
                }

                return true;
            })
        );
    };
}

/**
 * Pre-configured guards for common operations
 */

// Client Records Guards
export const canCreateClientsGuard: CanActivateFn = createRbacGuard(
    CaslAction.Create, 
    RbacSubjects.CLIENTS, 
    '/clients/records'
);

export const canUpdateClientsGuard: CanActivateFn = createRbacGuard(
    CaslAction.Update, 
    RbacSubjects.CLIENTS, 
    '/clients/records'
);

export const canDeleteClientsGuard: CanActivateFn = createRbacGuard(
    CaslAction.Delete, 
    RbacSubjects.CLIENTS, 
    '/clients/records'
);

// Products Guards
export const canCreateProductsGuard: CanActivateFn = createRbacGuard(
    CaslAction.Create, 
    RbacSubjects.PRODUCTS, 
    '/mpd/products'
);

export const canUpdateProductsGuard: CanActivateFn = createRbacGuard(
    CaslAction.Update, 
    RbacSubjects.PRODUCTS, 
    '/mpd/products'
);

export const canDeleteProductsGuard: CanActivateFn = createRbacGuard(
    CaslAction.Delete, 
    RbacSubjects.PRODUCTS, 
    '/mpd/products'
);

// Organizations Guards
export const canCreateOrganizationsGuard: CanActivateFn = createRbacGuard(
    CaslAction.Create, 
    RbacSubjects.ORGANIZATIONS, 
    '/mpd/organizations'
);

export const canUpdateOrganizationsGuard: CanActivateFn = createRbacGuard(
    CaslAction.Update, 
    RbacSubjects.ORGANIZATIONS, 
    '/mpd/organizations'
);

export const canDeleteOrganizationsGuard: CanActivateFn = createRbacGuard(
    CaslAction.Delete, 
    RbacSubjects.ORGANIZATIONS, 
    '/mpd/organizations'
);

// Users Guards
export const canCreateUsersGuard: CanActivateFn = createRbacGuard(
    CaslAction.Create, 
    RbacSubjects.USERS, 
    '/admin/users'
);

export const canReadUsersGuard: CanActivateFn = createRbacGuard(
    CaslAction.Read, 
    RbacSubjects.USERS, 
    '/admin/users'
);

export const canUpdateUsersGuard: CanActivateFn = createRbacGuard(
    CaslAction.Update, 
    RbacSubjects.USERS, 
    '/admin/users'
);

export const canDeleteUsersGuard: CanActivateFn = createRbacGuard(
    CaslAction.Delete, 
    RbacSubjects.USERS, 
    '/admin/users'
);

export const canManageUsersGuard: CanActivateFn = createRbacGuard(
    CaslAction.Manage, 
    RbacSubjects.USERS, 
    '/admin/users'
);

// System Configuration Guards
export const canManageSystemConfigGuard: CanActivateFn = createRbacGuard(
    CaslAction.Manage, 
    RbacSubjects.SYSTEM_CONFIG, 
    '/'
);

/**
 * Combined guards for complex scenarios
 */

// Guard that requires ANY of the specified permissions
export function createAnyPermissionGuard(
    permissions: Array<{ action: CaslAction; subject: RbacSubjects }>,
    redirectTo: string = '/unauthorized'
): CanActivateFn {
    return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        const store = inject(Store);
        const router = inject(Router);
        const permissionsService = inject(PermissionsService);
        const messageService = inject(MessageService);

        return store.select(AuthSelectors.selectAuthInitialized).pipe(
            filter((initialized) => initialized),
            take(1),
            switchMap(() => store.select(AuthSelectors.selectUser)),
            take(1),
            map((user) => {
                if (!user) {
                    return router.createUrlTree(['/auth/login']);
                }

                const hasAnyPermission = permissions.some(({ action, subject }) => 
                    permissionsService.can(action, subject)
                );
                
                if (!hasAnyPermission) {
                    messageService.add({
                        severity: 'error',
                        summary: 'Access Denied',
                        detail: 'You don\'t have permission to access this page',
                        life: 5000
                    });
                    
                    return router.createUrlTree([redirectTo]);
                }

                return true;
            })
        );
    };
}

// Guard that requires ALL of the specified permissions
export function createAllPermissionsGuard(
    permissions: Array<{ action: CaslAction; subject: RbacSubjects }>,
    redirectTo: string = '/unauthorized'
): CanActivateFn {
    return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        const store = inject(Store);
        const router = inject(Router);
        const permissionsService = inject(PermissionsService);
        const messageService = inject(MessageService);

        return store.select(AuthSelectors.selectAuthInitialized).pipe(
            filter((initialized) => initialized),
            take(1),
            switchMap(() => store.select(AuthSelectors.selectUser)),
            take(1),
            map((user) => {
                if (!user) {
                    return router.createUrlTree(['/auth/login']);
                }

                const hasAllPermissions = permissions.every(({ action, subject }) => 
                    permissionsService.can(action, subject)
                );
                
                if (!hasAllPermissions) {
                    messageService.add({
                        severity: 'error',
                        summary: 'Access Denied',
                        detail: 'You don\'t have sufficient permissions to access this page',
                        life: 5000
                    });
                    
                    return router.createUrlTree([redirectTo]);
                }

                return true;
            })
        );
    };
}
